import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { UserApi } from 'app/modules/dashboard/api/user.api';
import { MeActions } from './me.actions';
import {
  catchError,
  first,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { User } from 'app/models';
import { FollowApi } from 'app/modules/dashboard/api/follow.api';
import { PaginableUsersHttpResult } from 'app/modules/dashboard/models/users.model';
import { Store } from '@ngrx/store';
import {
  selectFollowers,
  selectFollowersPagination,
  selectFollowings,
  selectFollowingsPagination,
} from './me.selectors';
import { Pagination } from 'app/modules/dashboard/models/pagination.model';
import { EquipmentApi } from 'app/modules/dashboard/api/equipment.api';
import { Equipment } from 'app/modules/dashboard/models/equipment.model';
import { Settings } from 'app/modules/dashboard/models/settings.model';
import { ConfigApi } from 'app/modules/dashboard/api/config.api';
import { selectCountries } from '../countries/countries.selector';
import { Country } from '@kinomap-dev/ng-ui-lib';
import { CurrencyType } from 'app/services/gift-cards-plans.service';
import {
  GiftCardsProductsActions,
  SubscriptionsProductsActions,
} from '../products/products.actions';
import { SubscriptionsActions } from 'app/modules/dashboard/store/subscriptions/subscriptions.actions';

@Injectable()
export class MeEffects {
  constructor(
    private userApi: UserApi,
    private followApi: FollowApi,
    private equipmentApi: EquipmentApi,
    private configApi: ConfigApi,
    private actions$: Actions,
    private store: Store
  ) {}

  getProfile$: Observable<{ user: User }> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.getProfile),
      mergeMap(() =>
        this.userApi.getUser().pipe(
          map((user: User) => MeActions.setProfile({ user })),
          catchError(() => {
            if (localStorage.getItem('user_access_token')) {
              localStorage.removeItem('user_access_token');
            }
            return of(MeActions.setProfile({ user: null }));
          })
        )
      )
    );
  });

  getSettings$: Observable<{ settings: Settings }> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.getSettings),
      mergeMap(() =>
        forkJoin<[Country[], Settings]>([
          this.store.select(selectCountries).pipe(first()),
          this.configApi.getMySettings(),
        ]).pipe(
          map(([countries, settings]: [Country[], Settings]) => {
            const localLanguage: string =
              localStorage.getItem('lang') || 'fr-FR';
            const lang: string = localLanguage
              .split('-')[1]
              .toLocaleLowerCase();
            const currency: CurrencyType =
              (countries.find((country: Country) => {
                return country.code === lang;
              })?.currency as CurrencyType) || 'eur';
            localStorage.setItem('settings', JSON.stringify(settings));
            return MeActions.setSettings({
              settings: { ...settings, currency },
            });
          }),
          catchError((err: Error) => {
            console.error('Error fetching settings:', err);
            return of(MeActions.setSettings({ settings: null }));
          })
        )
      )
    );
  });

  getFollowers$: Observable<{
    followers: PaginableUsersHttpResult;
  }> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.getFollowers),
      concatLatestFrom(() => this.store.select(selectFollowers)),
      switchMap(([{ page, limit }]) => {
        // Si aucune page n'est définie, la page active dans le store est récupérée.
        if (!page) {
          this.store
            .select(selectFollowersPagination)
            .subscribe(
              (pagination: Pagination) =>
                (page = pagination?.current_page
                  ? pagination.current_page + 1
                  : 1)
            );
        }

        return this.followApi.getMyFollowers(page, limit).pipe(
          map((followers: PaginableUsersHttpResult) => {
            return MeActions.setFollowers({
              followers,
            });
          }),
          catchError(() =>
            of(
              MeActions.setFollowers({
                followers: { data: [], meta: null },
              })
            )
          )
        );
      })
    );
  });

  getFollowings$: Observable<{
    followings: PaginableUsersHttpResult;
  }> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.getFollowings),
      concatLatestFrom(() => this.store.select(selectFollowings)),
      switchMap(([{ page, limit }]) => {
        // Si aucune page n'est définie, la page active dans le store est récupérée.
        if (!page) {
          this.store
            .select(selectFollowingsPagination)
            .subscribe(
              (pagination: Pagination) =>
                (page = pagination?.current_page
                  ? pagination.current_page + 1
                  : 1)
            );
        }

        return this.followApi.getMyFollowings(page, limit).pipe(
          map((followings: PaginableUsersHttpResult) => {
            return MeActions.setFollowings({
              followings,
            });
          }),
          catchError(() =>
            of(
              MeActions.setFollowings({ followings: { data: [], meta: null } })
            )
          )
        );
      })
    );
  });

  getEquipments$: Observable<{ equipments: Equipment[] }> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.getEquipments),
      mergeMap(() =>
        this.equipmentApi.getMyEquipments().pipe(
          map((equipments: Equipment[]) => {
            return MeActions.setEquipments({ equipments });
          }),
          catchError(() => of(MeActions.setEquipments({ equipments: null })))
        )
      )
    );
  });

  getCurrency$: Observable<{ currency: CurrencyType }> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.getCurrency),
      mergeMap((action) => {
        this.store.dispatch(
          SubscriptionsProductsActions.getSubscriptionsProducts({
            currency: action.currency,
          })
        );
        this.store.dispatch(
          GiftCardsProductsActions.getGiftCardsProducts({
            currency: action.currency,
          })
        );
        return of(MeActions.setCurrency({ currency: action.currency }));
      })
    );
  });

  clear$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeActions.clear),
      map(() => {
        this.store.dispatch(SubscriptionsActions.reset());
        return { type: '[Me] Clear Complete' };
      })
    );
  });
}
